import { useSelector } from 'react-redux';
import { selectUser } from '../selectors/user';
import {
  getNumberOfChallenges,
  getNumberOfCompletedChallenges,
  getNumberOfValidGroups,
  getNumberOfCompletedGroups,
  isIncompleteProfile,
} from '../helpers/onBoardingHelper';
import { UserDataOutput } from '../@types';

type Options = {
  user?: UserDataOutput | null;
  readOnly?: boolean;
  separateCreditCheckScreen?: boolean;
};

/**
 * Shared hook for UI variables of the profile wizard "Continue" CTA used in web and mobile.
 * Computes visibility and progress data consistently across platforms.
 */
export const useProfileWizardContinue = (options: Options = {}) => {
  const {
    user: userProp,
    readOnly = false,
    separateCreditCheckScreen = false,
  } = options;

  const userFromRedux = useSelector(selectUser) as UserDataOutput | null;

  const user = userProp ?? userFromRedux;

  const totalChallenges = user
    ? getNumberOfChallenges(user, separateCreditCheckScreen)
    : 0;
  const completedChallenges = user ? getNumberOfCompletedChallenges(user) : 0;

  const numberOfValidGroups = user ? getNumberOfValidGroups(user) : 0;
  const numberOfCompletedGroups = user ? getNumberOfCompletedGroups(user) : 0;

  const progressPercent = numberOfValidGroups
    ? Math.round((numberOfCompletedGroups / numberOfValidGroups) * 100)
    : 0;

  const showContinueCTA = !!(
    user &&
    !readOnly &&
    isIncompleteProfile(user) &&
    completedChallenges < totalChallenges &&
    !user?.blocked
  );

  return {
    showContinueCTA,
    progressPercent,
    numberOfValidGroups,
    numberOfCompletedGroups,
    totalChallenges,
    completedChallenges,
  } as const;
};
